import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type AccessStage = {
  stage: "token" | "site" | "library" | "docx";
  status: "passed" | "failed" | "skipped";
  message: string;
  kind?: string;
  httpStatus?: number;
  graphCode?: string;
};
type AccessResult = { ok: boolean; stages: AccessStage[] };
const stageNames = { token: "Graph token", site: "Selected site", library: "Document library", docx: "Approved DOCX" };

export function SharePointAccessTest({ chatbotId, available }: { chatbotId: string; available: boolean }) {
  const [siteUrl, setSiteUrl] = useState("");
  const [library, setLibrary] = useState("");
  const [docxPath, setDocxPath] = useState("");
  const [approved, setApproved] = useState(false);
  const test = useMutation({
    mutationFn: async (): Promise<AccessResult> => {
      const response = await apiRequest("POST", `/api/chatbots/${chatbotId}/integrations/m365/test-sharepoint`, {
        siteUrl: siteUrl.trim(), library: library.trim() || null, docxPath: docxPath.trim() || null,
      });
      const result = await (response as Response).json();
      if (typeof result.ok !== "boolean" || !Array.isArray(result.stages))
        throw new Error("Invalid diagnostic response");
      return result;
    },
  });
  function edit(setter: (value: string) => void, value: string) {
    setter(value); setApproved(false); test.reset();
  }
  return (
    <section className="space-y-3 rounded-md border p-3" aria-labelledby="sharepoint-test-title" data-testid="sharepoint-access-test">
      <h3 id="sharepoint-test-title" className="text-sm font-medium">Test SharePoint access</h3>
      <p className="text-xs text-muted-foreground">
        Uses this chatbot&apos;s saved Microsoft 365 credentials. Checks your selected site, not directory or calendar access.
        No documents are indexed, no token is saved, and integration settings are unchanged.
      </p>
      <div className="space-y-1">
        <Label htmlFor="sp-test-site">SharePoint site URL</Label>
        <Input id="sp-test-site" type="url" maxLength={2048} value={siteUrl}
          placeholder="https://contoso.sharepoint.com/sites/Policies" disabled={test.isPending}
          onChange={(e) => edit(setSiteUrl, e.target.value)} data-testid="input-sp-test-site" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="sp-test-library">Document library (optional)</Label>
        <Input id="sp-test-library" maxLength={200} value={library} placeholder="Documents" disabled={test.isPending}
          onChange={(e) => edit(setLibrary, e.target.value)} data-testid="input-sp-test-library" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="sp-test-docx">Approved DOCX path (optional, relative to library)</Label>
        <Input id="sp-test-docx" maxLength={2048} value={docxPath} placeholder="Approved/Handbook.docx" disabled={test.isPending}
          onChange={(e) => edit(setDocxPath, e.target.value)} data-testid="input-sp-test-docx" />
        <p className="text-xs text-muted-foreground">Leave blank to skip file download. If supplied, only this document is downloaded and extracted in memory.</p>
      </div>
      <label className="flex items-start gap-2 text-xs">
        <input type="checkbox" checked={approved} disabled={test.isPending}
          onChange={(e) => setApproved(e.target.checked)} data-testid="checkbox-sp-test-approval" />
        <span>I approve a live access test of this site and, if specified, this DOCX.</span>
      </label>
      {!available && <p className="text-xs text-muted-foreground">Save and approve a real Microsoft 365 integration first. Mock mode does not contact SharePoint.</p>}
      <Button type="button" variant="outline" disabled={!available || !approved || !siteUrl.trim() || test.isPending}
        onClick={() => test.mutate()} data-testid="button-test-sharepoint">
        {test.isPending ? "Testing access…" : "Run SharePoint access test"}
      </Button>
      <div role="status" aria-live="polite" aria-atomic="true" data-testid="sharepoint-test-results">
        {test.isError && <p className="text-sm text-destructive">The access test could not be completed. Check your chatbot permissions and try again.</p>}
        {test.data && (
          <div className="space-y-2 text-xs">
            <p className="font-medium">{test.data.ok ? "Access checks passed" : "Access check failed"}</p>
            <ol className="space-y-2">
              {test.data.stages.map((stage) => (
                <li key={stage.stage} data-testid={`sp-stage-${stage.stage}`} className={stage.status === "failed" ? "text-destructive" : ""}>
                  <strong>{stageNames[stage.stage]}: {stage.status}</strong>
                  {stage.httpStatus && <span> (HTTP {stage.httpStatus})</span>}
                  <p>{stage.message}</p>
                  {stage.kind && <p>Error type: {stage.kind}</p>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </section>
  );
}